import { defineStore, storeToRefs } from 'pinia'
import { useAuthStore } from './auth'
import { useSubscriptionStore } from './subscription'
import { Auth0 } from '@/config/auth'
import { useLocalStorage } from '@vueuse/core'
import { fetchActors, freshenUserLink } from '@/composables/api/auth/index'

type ActorState = {
  actors?: { [index: string]: any }
  loadingActors: boolean
  switchingActor: boolean
  currentActor?: string
  previousActor?: string
  intercomUserJwt?: string
}

const storageKey = (actorType: 'currentActor' | 'previousActor', userId?: string) =>
  `${actorType}|${userId ? userId : ''}`

const state = (): ActorState => {
  const userId = Auth0.user.value?.sub
  const existingActorStorage = useLocalStorage<string | undefined>(
    storageKey('currentActor', userId),
    undefined
  )
  const previousExistingActorStorage = useLocalStorage<string | undefined>(
    storageKey('previousActor', userId),
    undefined
  )
  return {
    actors: undefined,
    switchingActor: false,
    loadingActors: false,
    currentActor: existingActorStorage.value,
    previousActor: previousExistingActorStorage.value,
    intercomUserJwt: undefined
  }
}

export const useActorStore = defineStore('actor', {
  state,
  actions: {
    async initialize() {
      const subscriptionStore = useSubscriptionStore()
      await this.retrieveCurrentActor(true)
      await subscriptionStore.initialize()
    },
    changeActor(actorId: string) {
      this.switchingActor = true
      if (this.currentActor) this.setPreviousActor(this.currentActor)
      this.setCurrentActor(actorId)
      this.switchingActor = false
    },
    setActors(actors: { string: any }) {
      this.actors = actors
      const userId = Auth0.user.value?.sub
      const existingActorStorage = useLocalStorage<string | undefined>(
        storageKey('currentActor', userId),
        undefined
      )
      const localActor = existingActorStorage.value
      const actorList = Object.keys(actors)
      if (localActor && actorList.includes(localActor)) {
        this.setCurrentActor(localActor)
        return
      }
      const currentActor = actorList[0]
      if (currentActor && this.currentActor === undefined) {
        this.setCurrentActor(currentActor)
        return
      }
    },
    setCurrentActor(currentActor: string) {
      const userId = Auth0.user.value?.sub
      const existingActorStorage = useLocalStorage<string | undefined>(
        storageKey('currentActor', userId),
        undefined
      )
      existingActorStorage.value = currentActor
      this.currentActor = currentActor
    },
    setPreviousActor(actor: string) {
      const userId = Auth0.user.value?.sub
      const existingActorStorage = useLocalStorage<string | undefined>(
        storageKey('previousActor', userId),
        undefined
      )
      existingActorStorage.value = actor
      this.previousActor = actor
    },
    setIntercomUserJwt(intercomUserJwt: string) {
      this.intercomUserJwt = intercomUserJwt
    },
    async refreshActors() {
      const authStore = useAuthStore()
      const { accessToken, userId } = storeToRefs(authStore)
      if (userId?.value?.startsWith('samlp')) {
        try {
          await freshenUserLink(accessToken?.value)
        } catch {
          Auth0.logout({
            logoutParams: {
              returnTo: `${window.location.origin}/sso-error`
            }
          })
        }
      }
      const { actors, intercomUserJwt } = await fetchActors(accessToken?.value)
      this.setActors(actors)
      this.setIntercomUserJwt(intercomUserJwt)
      return actors
    },
    async retrieveCurrentActor(forceRefresh = false): Promise<string> {
      const authStore = useAuthStore()
      if (this.currentActor && !forceRefresh && authStore.accessToken && this.actors) {
        return this.currentActor
      }
      const authToken = await authStore.checkToken()
      if (authToken) {
        if (!this.actors) {
          if (this.loadingActors) {
            do {
              await sleep(500)
            } while (this.loadingActors && !this.currentActor)
          } else {
            this.loadingActors = true
            await this.refreshActors()
            this.loadingActors = false
          }
        }
      }
      // @ts-ignore
      // hard to see, but this is actually guaranteed to be string at this point
      // because of the refresh actors call
      return this.currentActor
    },
    hasAccess(actions: string[] | undefined): boolean {
      const currentActor = this.getCurrentActor
      if (!this.actors || !this.currentActor) return false
      if (currentActor.access_level === 'super_user') return true
      const currentActorPermissions: Set<string> = new Set(currentActor.actions)
      const requestedPermissions = new Set(actions)
      for (const permission of requestedPermissions) {
        if (!currentActorPermissions.has(permission)) {
          return false
        }
      }
      return true
    }
  },
  getters: {
    getCurrentActor(state: ActorState): any | undefined {
      return state.actors && state.currentActor ? state.actors[state.currentActor] : undefined
    },
    getGroupedActors(state: ActorState): Record<string, any> | undefined {
      if (state.actors) {
        const subscriberGroupBy = Map.groupBy(
          Object.entries(state.actors),
          ([, actor]) => actor.subscriber_group_name
        )
        const subscriberGroups: Record<string, any> = {}
        for (const [key, value] of subscriberGroupBy) {
          // Filter out actors where subscriber_group or subscriber_group_name is empty/null
          const filteredValues = value.filter(
            ([, actor]) => actor.subscriber_group && actor.subscriber_group_name
          )

          // Only add to subscriberGroups if there are valid actors after filtering
          if (filteredValues.length > 0) {
            const newValues = filteredValues.map(([, actor]) => actor)
            subscriberGroups[key] = newValues
          }
        }
        return subscriberGroups
      }
    },
    getCurrentTrackingData(state: ActorState) {
      if (state.actors && state.currentActor) {
        const currentActor = state.actors[state.currentActor]
        if (currentActor) {
          const data = {
            subscriberGroup: currentActor.subscriber_group_name,
            actorId: state.currentActor,
            role: currentActor.access_level,
            subscriberGroupId: currentActor.subscriber_group,
            intercomUserJwt: state.intercomUserJwt
          }
          return data
        }
      }
      return undefined
    }
  }
})

const sleep = (time: number) => new Promise((cb) => setTimeout(cb, time))
